/* eslint-disable complexity */

import { Button, Typography, Tooltip } from '@scale/scaleui';
import { InteractionStepTaskComponent } from '../../InteractionStep';
import { Alert, CardAlert } from '../../TempComponents';
import useChatCompletion from 'react-scale/hooks/chat/useChatCompletion';
import { useCallback, useEffect, useMemo, useState, useContext, useRef } from 'react';
import { CandidateState, Message } from '@scale/shared/types/chat-completion';
import pluralize from 'pluralize';
import {
  InteractionStepType,
  CandidateMessageWithMetadata,
  ProvidedModelResponse,
  AssistantContent,
  MessageRole,
  ChatTaskContextType,
  ChatAttachment,
  ModelResponseSelectorStepResponse,
  EXISTING_CANDIDATE_ID,
  CreateCompletionPair,
  PREVIOUS_TURN_CANDIDATE_ID,
  UIGenerationMetadata,
  UIGenerationEnforcementMode,
} from '@scale/shared/types/chat';
import { extractPrefilledMessages } from '@scale/shared/chat/response/validation';
import _ from 'lodash';
import { Transition } from '@headlessui/react';
import {
  buildCandidatesWithId,
  shuffleCandidates,
  isModelSelectionConsistent,
  generateShuffledRange,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/utils';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { LikertScale } from './LikertScale';
import {
  useAllResponsesForStepType,
  useChatHistory,
  useChatStoreExtract,
  useMostRecentPrompt,
  useMostRecentPromptAttachments,
  useMostRecentResponse,
  useMostRecentResponseForStepId,
  useMostRecentResponseForStepType,
  useMostRecentResponseInTurn,
  useMostRecentStepParamsForStepType,
  useNumberOfTurns,
  useProcessSupervisionLimitChatHistory,
  useStepParams,
  useStepResponse,
  useMostRecentPromptForStepId,
} from '../../../Chat/hooks/ChatStore.hooks';
import { JustificationEditor } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseSelector/JustificationEditor';
import { ReviewLevel } from '@scale/shared-types';
import { useStepSubmitHandler } from '../../hooks/useStepSubmitHandler';
import useResponseSuggestions from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseSuggestions/hooks/useResponseSuggestions';
import { useConfigureChatMessageDisplay } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useConfigureChatMessageDisplay';
import ModelResponseSelectorTabsWrapper from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseSelector/ModelResponseSelectorTabsWrapper';
import { StepWidthWrapper, StepWidth } from '../../step/StepWidthWrapper';
import TutorialFieldContainer from 'react-scale/components/scalers/tasks/tutorial/TutorialFieldContainer';

import { StepCard } from '../../step/StepCard';

import {
  generateChatPrompt,
  formatChatHistory,
  formatPromptWithTemplate,
  unrandomizeText,
  getInitialLikertScore,
} from './helpers';
import { CandidateCards } from './CandidateCards';
import { ObjectivityQuestion } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ModelResponseSelector/ObjectivityQuestion';
import { ModelResponseSelectorCardHeader } from './ModelResponseSelectorCardHeader';
import { useChatModels } from './useChatModels';
import { ChatTaskParamsStages } from '@scale/shared/chat/steps';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import crypto from 'crypto';
import { useEventBus } from 'react-scale/components/scalers/tasks/ThreadedInteraction/events';
import { PopulatedProjectModelConfigDTO } from '@scale/customer-app-shared';
import logger from '@scale/frontend-lib/logging';

export const LikertNoPreferenceId = 'no_preference';

function generateOutput(candidate: CandidateMessageWithMetadata | undefined): string {
  if (!candidate) {
    return '';
  }
  // BX0K Agent Completion output
  if (
    candidate?.message?.messageMetadata?.raw_tool_response_messages &&
    candidate?.message?.messageMetadata?.raw_tool_response_messages.length > 0
  ) {
    const texts = [];
    for (const toolCallMsg of candidate.message.messageMetadata?.raw_tool_response_messages) {
      texts.push(`Tool Call Id: ${toolCallMsg.tool_call_id}:`);
      try {
        texts.push(
          `\`\`\`json\n${JSON.stringify(JSON.parse(toolCallMsg.content), null, 2)}\n\`\`\``,
        );
      } catch {
        // If the content is not valid JSON, just display it as is
        texts.push(toolCallMsg.content);
      }
    }
    return texts.join('\n\n');
  }
  // all other cases
  return candidate.message.content;
}
function getCombinedSystemPrompt(
  systemPrompt: string | undefined,
  appendedSystemPrompt: string | undefined,
  excludeSystemPrompt: boolean | undefined,
) {
  if (excludeSystemPrompt) {
    return undefined;
  }
  if (!systemPrompt && !appendedSystemPrompt) {
    return undefined;
  }
  if (!systemPrompt) {
    return appendedSystemPrompt;
  }
  if (!appendedSystemPrompt) {
    return systemPrompt;
  }
  return `${systemPrompt} ${appendedSystemPrompt}`;
}

const maybeReplaceCandidateEndpointWithProjectModelConfigurationMapping = (
  candidate: CandidateMessageWithMetadata,
  projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>,
) => {
  if (
    candidate.endpoint?.projectModelConfigurationKey &&
    projectModelConfigurationMapping &&
    projectModelConfigurationMapping[candidate.endpoint.projectModelConfigurationKey]
  ) {
    return _.merge({}, candidate, {
      endpoint: projectModelConfigurationMapping[candidate.endpoint.projectModelConfigurationKey],
    });
  }
  return candidate;
};

const ModelResponseSelectorTaskComponent: InteractionStepTaskComponent = ({
  readOnly,
  skipContext,
  complete,
  responseIndex,
  isPinnedView,
  isPinnable,
}) => {
  const taskContext = useContext(ChatTaskContext);

  // hacky flag to remove the last user message if it is empty
  const chatRemoveLastUserMessageIfEmpty = useUnleashFeatureFlag(
    'chat-remove-last-user-message-if-empty',
    { projectId: taskContext.projectId },
    false,
  );
  const shouldHackilyUpdateNextMultiTurnContinueStep = useUnleashFeatureFlag(
    'hackily-update-next-multi-turn-continue-step',
    { projectId: taskContext.projectId },
    false,
  );

  const interactionHistory = useChatStoreExtract(state => state.interactionHistory);
  const setInteractionHistory = useChatStoreExtract(state => state.setInteractionHistory);
  const setProjectModelConfigurationMapping = useChatStoreExtract(
    state => state.setProjectModelConfigurationMapping,
  );

  const step = useStepParams<InteractionStepType.ModelResponseSelector>(responseIndex);
  const response = useStepResponse<ModelResponseSelectorStepResponse>(responseIndex);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const config = useChatStoreExtract(state => state.config);
  const markStepAsIncompletedByIndex = useChatStoreExtract(
    state => state.markStepAsIncompletedByIndex,
  );
  const projectModelConfigurationMapping = useChatStoreExtract(
    state => state.projectModelConfigurationMapping,
  );

  const areAutoraterSuggestionsEnabled = useUnleashFeatureFlag(
    'chat-autorater-suggestions',
    {
      projectId: taskContext.projectId,
    },
    false,
  );

  // Params and their defaults
  // Inject worker ID if needed
  (step.params?.chat_models || []).forEach(model => {
    if (model?.params?.useWorkerLocation) {
      const workerId = taskContext?.workerId;
      model.params.workerId = workerId;
    }
  });

  const chatHistory = useChatHistory(responseIndex);
  const chatMessages: Message[] = useMemo(() => {
    // For MRS steps in the After stage, we can assume this response should replace the last turn's response in the history
    if (step.stage === ChatTaskParamsStages.After) {
      return chatHistory.slice(0, -1);
    }
    return chatHistory;
  }, [chatHistory, step.stage]);

  const numberOfTurns = useNumberOfTurns(responseIndex);

  const mostRecentPromptAttachments = useMostRecentPromptAttachments(responseIndex);
  const attachments = useMemo(() => {
    // Handle passing down original prompt attachments if config is enabled
    const originalAttachments =
      chatMessages.find((message: Message) => message.role !== MessageRole.System)?.attachments ||
      [];

    let consolidatedAttachments = mostRecentPromptAttachments;
    // Add the first turn attachments to the most recent prompt's attachments when passDownOriginalAttachments is enabled.
    // Note: passDownOriginalAttachments can only be set if resetChatHistoryOnEachTurn is set.
    // Note: On the first turn, the most recent prompt's attachments is the same as the first turn attachments (original attachments), so this is not necessary.
    if (
      taskContext.resetChatHistoryOnEachTurn &&
      taskContext.passDownOriginalAttachments &&
      numberOfTurns > 1
    ) {
      consolidatedAttachments = [...originalAttachments, ...mostRecentPromptAttachments];
    }
    return consolidatedAttachments;
  }, [
    mostRecentPromptAttachments,
    taskContext.resetChatHistoryOnEachTurn,
    taskContext.passDownOriginalAttachments,
    chatMessages,
    numberOfTurns,
  ]);

  const latestModelResponseInPreviousTurn = useMostRecentResponseInTurn(numberOfTurns - 2);

  const mostRecentMultiTurnContinueResponse = useMostRecentResponseForStepType(
    InteractionStepType.MultiTurnContinue,
  );
  const previousModelResponse = useMostRecentResponseForStepType(
    InteractionStepType.ModelResponseSelector,
    responseIndex,
  );
  const latestModelResponseEditorResponse = useMostRecentResponseForStepType(
    InteractionStepType.ModelResponseEditor,
    responseIndex,
  );
  const allProcessSupervisionResponses = useAllResponsesForStepType(
    InteractionStepType.ProcessSupervision,
    responseIndex,
  );
  const allExperimentalToolUseResponses = useAllResponsesForStepType(
    InteractionStepType.ExperimentalToolUse,
    responseIndex,
  );
  const previousTextCollection = useMostRecentResponseForStepId(
    step.params.use_last_response_for_step_id,
    responseIndex,
  );
  const textCollectionPerResponseStepResponse = useMostRecentResponseForStepType(
    InteractionStepType.TextCollectionPerResponse,
    responseIndex,
  );
  const textCollectionPerResponseStepParams = useMostRecentStepParamsForStepType(
    InteractionStepType.TextCollectionPerResponse,
    responseIndex,
  );

  const [codeGenerationEnforcementPassed, setCodeGenerationEnforcementPassed] =
    useState<boolean>(true);

  useEffect(() => {
    if (
      !codeGenerationEnforcementPassed &&
      (step.params.ui_generation_enforcement_mode ?? UIGenerationEnforcementMode.None) !==
        UIGenerationEnforcementMode.None
    ) {
      markStepAsIncompletedByIndex(responseIndex);
    }
  }, [codeGenerationEnforcementPassed, markStepAsIncompletedByIndex, responseIndex]);

  const {
    chatMessageDisplayConfig,
    useMarkdownDisplay,
    updateMarkdownConfig,
    toggleChatMessageDisplayType,
  } = useConfigureChatMessageDisplay();

  const { onSubmit } = useStepSubmitHandler({
    responseIndex,
  });

  const mostRecentPromptBase = useMostRecentPrompt(responseIndex);
  const mostRecentPrompt = useMemo(() => {
    if (step.params?.appended_user_message) {
      return `${mostRecentPromptBase}\n${step.params?.appended_user_message}`;
    }

    return mostRecentPromptBase;
  }, [step.params?.appended_user_message, mostRecentPromptBase]);
  const mostRecentPromptForStepId = useMostRecentPromptForStepId(
    responseIndex,
    step.params.prompt_input_step_id_to_use_for_current_turn,
  );

  const chatPrompt = useMemo(() => {
    return generateChatPrompt(
      step,
      config,
      allExperimentalToolUseResponses,
      allProcessSupervisionResponses,
      mostRecentPrompt,
      chatMessages,
    );
  }, [
    step,
    config,
    allExperimentalToolUseResponses,
    allProcessSupervisionResponses,
    mostRecentPrompt,
    chatMessages,
  ]);

  const mostRecentResponse = useMostRecentResponse(responseIndex);
  const lastMessage: { content: string; attachments: ChatAttachment[] } = useMemo(() => {
    const previousModelResponseContext = previousModelResponse?.context;

    let attachments: ChatAttachment[] = [];
    if (previousModelResponseContext) {
      const selectedCandidate = previousModelResponseContext.candidates.find(
        candidate => candidate.id === previousModelResponseContext.selectedId,
      );
      if (selectedCandidate && selectedCandidate.attachments) {
        attachments = selectedCandidate.attachments;
      }
    }

    // For Bulba ICE sxs in L4 - deprecate when not in use
    if (step.params.use_most_recent_output) {
      return { content: latestModelResponseEditorResponse?.output as string, attachments };
    } else {
      return { content: mostRecentResponse, attachments };
    }
  }, [
    step.params.use_most_recent_output,
    previousModelResponse,
    latestModelResponseEditorResponse,
    mostRecentResponse,
  ]);

  const assistantProvidedMessages = useMemo(() => {
    const initialMessages = !step.params.skip_initial_messages ? config.initialMessages ?? [] : [];
    const { taskingAssistantMessages } = extractPrefilledMessages(initialMessages);
    const turnIndex = numberOfTurns - 1;

    let assistantProvidedMessages: AssistantContent[] = [];
    if (turnIndex < taskingAssistantMessages.length) {
      assistantProvidedMessages = taskingAssistantMessages[turnIndex].options ?? [];
    }
    return assistantProvidedMessages;
  }, [step.params.skip_initial_messages, config.initialMessages, numberOfTurns]);

  const onlyUseExistingMessages =
    assistantProvidedMessages.length > 0 && !!step.params.only_use_existing_initial_messages;
  const { chatModels, chatModelsError, chatModelsLoading } = useChatModels({
    step,
    response,
    previousModelResponse: previousModelResponse ?? undefined,
    taskContext,
    onlyUseExistingMessages,
  });

  const providedResponses: ProvidedModelResponse[] = useMemo(() => {
    // Add the assistant initial messages if they exist
    const responses = [
      ...(step.params.provided_model_responses || []),
      ...(assistantProvidedMessages || []),
    ];

    // TODO (matthew / allen): For RLHF-RTL, deprecate when not in use
    // This specifically needs to be displayed before the most recent response
    if (
      step.params.use_previous_turn_model_response_as_option &&
      latestModelResponseInPreviousTurn
    ) {
      responses.push({
        id: PREVIOUS_TURN_CANDIDATE_ID,
        content: latestModelResponseInPreviousTurn,
      });
    }

    // Add the most recent response if enabled
    if (
      step.params.use_existing_model_response_as_option &&
      step.params.include_all_responses_from_most_recent_response
    ) {
      const previousModelResponseContext = previousModelResponse?.context;
      if (previousModelResponseContext) {
        previousModelResponseContext.candidates.forEach(candidate => {
          responses.push({
            id: candidate.id,
            content: candidate.message.content,
            attachments: candidate.message.attachments,
          });
        });
      }
    } else if (
      step.params.use_existing_model_response_as_option &&
      lastMessage &&
      !_.isNil(lastMessage.content)
    ) {
      /**
       * Hardcoded to debug issue for this specific project
       */
      if (taskContext.projectId === '67cabd457e9ab121b9ff06b4') {
        logger.info('use_existing_model_response_as_option', {
          assignmentId: taskContext.assignmentId,
          include_all_responses_from_most_recent_response:
            step.params.include_all_responses_from_most_recent_response,
          use_existing_model_response_as_option: step.params.use_existing_model_response_as_option,
          lastMessage: lastMessage.content,
        });
      }
      const recentResponse: AssistantContent = {
        id: EXISTING_CANDIDATE_ID,
        content: lastMessage.content,
      };
      if (step.params.propagate_response_attachments) {
        recentResponse.attachments = lastMessage.attachments;
      }
      responses.push(recentResponse);
    }

    return responses;
  }, [
    assistantProvidedMessages,
    step.params.provided_model_responses,
    step.params.use_existing_model_response_as_option,
    step.params.propagate_response_attachments,
    lastMessage,
    step.params.use_previous_turn_model_response_as_option,
    step.params.include_all_responses_from_most_recent_response,
    latestModelResponseInPreviousTurn,
    previousModelResponse,
    taskContext.projectId,
    taskContext.assignmentId,
  ]);

  const metadata = useMemo(() => {
    const currMetadata: { previousTextCollection?: any } = {};
    if (step.params.use_last_response_for_step_id) {
      currMetadata.previousTextCollection = previousTextCollection;
    }
    return currMetadata;
  }, [step.params.use_last_response_for_step_id, previousTextCollection]);

  const toolUseOptions = {
    shouldExecuteTools: step.params.should_execute_tools ?? false,
    toolIdRegex: step.params.tool_parsing_params?.toolIdRegex,
    toolInputRegex: step.params.tool_parsing_params?.toolInputRegex,
    toolOutputRegex: step.params.tool_parsing_params?.toolOutputRegex,
    reconstructionTemplate: step.params.tool_parsing_params?.reconstructionTemplate,
  };

  const chatCompletionHook = useChatCompletion({
    chatApiEndpoints: chatModels,
    providedModelResponses: providedResponses,
    projectId: taskContext.projectId || null,
    useCase: 'chat-main model-response-selector simple',
    options: {
      chatBenchmarkConfig: taskContext.chatBenchmarkConfig,
      toolUseOptions,
      omitAttachments: step.params.omit_attachments,
      enableSelectionWithErrors: step.params.enable_selection_with_errors,
      maxModelResponseErrorsForSelection: step.params.max_model_response_errors_for_selection,
      projectModelConfigurationMapping,
      templateVariables: config.templateVariables,
      isTaskPreview: taskContext.isTaskPreview,
    },
    metadata,
    pushInteractionHistoryOnTurn: (pair: CreateCompletionPair) => {
      const previousInteractions = interactionHistory[numberOfTurns] || [];
      const newInteractions = [
        ...previousInteractions,
        { ...pair, context: { ...pair.context, stepId: step.id } },
      ];
      interactionHistory[numberOfTurns] = newInteractions;
      setInteractionHistory({ ...interactionHistory });
    },
    stepId: step.id,
    responseIndex,
    setProjectModelConfigurationMapping: (
      projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>,
    ) => {
      setProjectModelConfigurationMapping(projectModelConfigurationMapping);
    },
  });

  const anyCandidateIsLoading = chatCompletionHook?.candidates.some(
    candidate => candidate.state === CandidateState.Loading,
  );

  const areCandidatesPrefilled = skipContext?.candidates && skipContext.candidates.length > 0;
  const validPrefilledDisplayOrder =
    areCandidatesPrefilled && skipContext?.displayOrder?.length === skipContext?.candidates?.length
      ? skipContext.displayOrder
      : undefined;

  /*
   * To ensure that candidateOrder is initialized properly, we infer the number of models
   * from the params instead of relying on the chatModels array, which may not be loaded yet
   */
  const numChatModels = onlyUseExistingMessages ? 0 : step.params.chat_models.length;
  const numCandidates = numChatModels + providedResponses.length;
  const initialCandidateOrder = getCandidateOrder(taskContext, numCandidates);

  const shouldUseLastModelResponses = step.params.use_last_model_responses;
  const lastModelSelectorContext = shouldUseLastModelResponses
    ? previousModelResponse?.context
    : undefined;

  const [candidateOrder, setCandidateOrder] = useState<number[]>(
    validPrefilledDisplayOrder ||
      lastModelSelectorContext?.displayOrder ||
      response?.context.displayOrder ||
      initialCandidateOrder,
  );

  const maybeReplaceExistingCandidate = useCallback(
    (candidate: CandidateMessageWithMetadata): CandidateMessageWithMetadata => {
      const shouldNotReplaceExisting =
        step.params.use_last_model_responses && !step.params.use_existing_model_response_as_option;
      return !shouldNotReplaceExisting &&
        candidate.sourceId === EXISTING_CANDIDATE_ID &&
        candidate.state === CandidateState.Completed
        ? {
            ...candidate,
            message: {
              ...candidate.message,
              content: lastMessage.content,
            },
          }
        : candidate;
    },
    [
      step.params.use_last_model_responses,
      step.params.use_existing_model_response_as_option,
      lastMessage.content,
    ],
  );

  const candidates = useMemo(() => {
    // Use the previous step's candidates if using last model responses
    if (shouldUseLastModelResponses && lastModelSelectorContext) {
      return lastModelSelectorContext.candidates.map((candidate: CandidateMessageWithMetadata) => {
        const newCandidate = maybeReplaceCandidateEndpointWithProjectModelConfigurationMapping(
          candidate,
          projectModelConfigurationMapping,
        );

        return step.params.replace_last_selected_candidate_with_existing &&
          newCandidate.id === lastModelSelectorContext.selectedId
          ? {
              ...newCandidate,
              message: {
                ...candidate.message,
                content: lastMessage.content,
              },
            }
          : newCandidate;
      });
    }

    // Use the existing response's candidates if no active fetching is happening
    if (response && response?.context?.candidates && chatCompletionHook.candidates.length === 0) {
      return response.context.candidates.map((candidate: CandidateMessageWithMetadata) => {
        return maybeReplaceCandidateEndpointWithProjectModelConfigurationMapping(
          candidate,
          projectModelConfigurationMapping,
        );
      });
    }

    // Use prefetched candidates if they exist
    if (areCandidatesPrefilled) {
      const candidates = [...skipContext.candidates];
      let index = candidates.length;
      for (const predefinedResponse of providedResponses) {
        candidates.push({
          index,
          state: CandidateState.Completed,
          message: {
            role: MessageRole.Assistant,
            content: predefinedResponse.content,
            attachments: predefinedResponse?.attachments,
            modelEvalEndpointId: predefinedResponse?.modelEvalEndpointId,
          },
          sourceId: predefinedResponse.id,
        });
        index += 1;
      }
      return candidates;
    }

    // Use the fetched candidates
    return chatCompletionHook.candidates;
  }, [
    response,
    areCandidatesPrefilled,
    skipContext,
    chatCompletionHook.candidates,
    providedResponses,
    shouldUseLastModelResponses,
    lastModelSelectorContext,
    step.params.replace_last_selected_candidate_with_existing,
    lastMessage,
    projectModelConfigurationMapping,
  ]);

  const generateReactUIGeneratedUrlWithMetadataFromCandidate = useCallback(
    (
      candidate: CandidateMessageWithMetadata,
    ): { reactUIGeneratedUrl?: string } & UIGenerationMetadata => {
      return {
        reactUIGeneratedUrl: candidate.reactUIGeneratedUrl,
        lastUIGenerationTimestamp: candidate.lastUIGenerationTimestamp,
        lastRunLanguage: candidate.lastRunLanguage,
        lastRunResult: candidate.lastRunResult,
        lastCodeRunHash: candidate.lastCodeRunHash,
        lastErrorMessage: candidate.lastErrorMessage,
      };
    },
    [],
  );

  const [reactGeneratedUrlWithMetadataByModel, setReactGeneratedUrlWithMetadataByModel] = useState<
    Record<string, { reactUIGeneratedUrl?: string } & UIGenerationMetadata>
  >(() => {
    const res = candidates?.reduce((acc, candidate) => {
      acc[candidate.id] = generateReactUIGeneratedUrlWithMetadataFromCandidate(candidate);
      return acc;
    }, {} as Record<string, { reactUIGeneratedUrl?: string } & UIGenerationMetadata>);
    return res || {};
  });

  const candidatesWithId: CandidateMessageWithMetadata[] = useMemo(() => {
    const resCandidates = buildCandidatesWithId(
      candidates.map(candidate => maybeReplaceExistingCandidate(candidate)),
      chatModels,
    );

    return resCandidates.map((candidate: CandidateMessageWithMetadata) => {
      const newCandidate = maybeReplaceCandidateEndpointWithProjectModelConfigurationMapping(
        candidate,
        projectModelConfigurationMapping,
      );

      const metadata = reactGeneratedUrlWithMetadataByModel[candidate.id] || {};

      return _.merge({}, newCandidate, metadata);
    });
  }, [
    candidates,
    chatModels,
    maybeReplaceExistingCandidate,
    reactGeneratedUrlWithMetadataByModel,
    projectModelConfigurationMapping,
  ]);

  const shuffledCandidates = useMemo(() => {
    if (shouldUseLastModelResponses && lastModelSelectorContext) {
      // Use the same order as the one last displayed if using previous responses
      return shuffleCandidates(candidatesWithId, lastModelSelectorContext.displayOrder);
    }
    return shuffleCandidates(candidatesWithId, candidateOrder);
  }, [candidatesWithId, candidateOrder, shouldUseLastModelResponses, lastModelSelectorContext]);

  const {
    isEnabled: isProcessSupervisionLimitChatHistoryEnabled,
    chatHistory: processSupervisionLimitedChatHistory,
    promptRole: processSupervisionPromptRole,
  } = useProcessSupervisionLimitChatHistory(responseIndex);

  // User prompts may be a function role, but they should default to the user role
  const role = mostRecentMultiTurnContinueResponse?.context?.role || MessageRole.User;
  const skipPromptInput = mostRecentMultiTurnContinueResponse?.context?.skipPromptInput || false;

  const [submittedPrompt, setSubmittedPrompt] = useState<string | undefined>(undefined);
  const hasSubmittedRef = useRef(false);

  const validateAndSubmitPrompt = useCallback(
    async (chatPrompt: string) => {
      if (hasSubmittedRef.current) {
        return;
      }
      hasSubmittedRef.current = true;
      if (skipPromptInput) {
        // Clear the prompt if we are skipping the prompt input
        chatPrompt = '';
      }
      setSubmittedPrompt(chatPrompt);

      if (chatModels.length > 0) {
        setCandidateOrder(
          validPrefilledDisplayOrder || getCandidateOrder(taskContext, numCandidates),
        );
      }

      if (areCandidatesPrefilled || (shouldUseLastModelResponses && lastModelSelectorContext)) {
        return;
      }

      if (config.userPromptTemplate?.length) {
        chatPrompt = formatPromptWithTemplate(
          chatPrompt,
          config.userPromptTemplate,
          config.templateVariables,
        );
      }

      const selectedModelIndex = previousModelResponse?.context?.selectedIndex;
      const mostRecentMessageMetadata =
        !_.isNil(selectedModelIndex) &&
        selectedModelIndex !== -1 &&
        previousModelResponse?.context?.candidates.length &&
        previousModelResponse?.context?.candidates.length > selectedModelIndex
          ? previousModelResponse?.context?.candidates[selectedModelIndex]?.message.messageMetadata
          : undefined;

      // Handle passing context within turn for 2+ MRS steps
      const currentTurnKey = String(numberOfTurns); // e.g. "1", "2", etc.
      const turnInteractions = (interactionHistory as Record<string, any[]>)[currentTurnKey] || [];
      const currentTurnMessages: Message[] = [];

      if (turnInteractions.length > 0) {
        const lastInteraction = turnInteractions[turnInteractions.length - 1];

        if (Array.isArray(lastInteraction.request.messages)) {
          lastInteraction.request.messages.forEach((reqMsg: any) => {
            currentTurnMessages.push({
              role: reqMsg.role,
              content: reqMsg.content,
              messageMetadata: reqMsg.messageMetadata,
            });
          });
        }

        if (lastInteraction.response?.content) {
          currentTurnMessages.push({
            role: MessageRole.Assistant,
            content: lastInteraction.response.content,
            messageMetadata: lastInteraction.response?.messageMetadata,
          });
        }
      }

      const historyChopped = formatChatHistory(chatMessages, {
        systemPrompt: getCombinedSystemPrompt(
          config.systemPrompt,
          step.params.appended_system_prompt,
          step.params.exclude_system_prompt,
        ),
        systemPromptGptOnly: config.systemPromptGptOnly,
        userPromptTemplate: config.userPromptTemplate,
        templateVariables: config.templateVariables,
        alwaysAddSystemPromptAsFirstMessage: taskContext.alwaysAddSystemPromptAsFirstMessage,
        useFirstInitialMessage: step.params.use_first_initial_message,
        resetChatHistoryOnEachTurn: taskContext.resetChatHistoryOnEachTurn,
        providedChatHistoryJson: config.providedChatHistoryJson,
        includeCurrentTurnInChatHistory: step.params.include_current_turn_in_chat_history,
        currentTurnMessages,
        mostRecentModelResponse: previousModelResponse?.output,
        mostRecentPromptForStepId,
        mostRecentMessageMetadata,
      });

      let skipPromptInputOnRefetch = false;
      if (chatRemoveLastUserMessageIfEmpty && chatPrompt === '') {
        skipPromptInputOnRefetch = true;
      }

      if (isProcessSupervisionLimitChatHistoryEnabled) {
        chatCompletionHook.fetchCandidates(
          {
            role: processSupervisionPromptRole,
            content: chatPrompt,
          },
          processSupervisionLimitedChatHistory,
          false, // systemPromptGptOnly
          null, // additionalEndpointParams
          true, // skipEarlyReturn
        );
      } else {
        chatCompletionHook.fetchCandidates(
          {
            role,
            content: chatPrompt,
            ...(attachments.length > 0 ? { attachments } : {}),
            ...(skipPromptInput || skipPromptInputOnRefetch
              ? { skipMessage: skipPromptInput || skipPromptInputOnRefetch }
              : {}),
          },
          historyChopped,
          config.systemPromptGptOnly,
        );
      }
    },
    [
      chatRemoveLastUserMessageIfEmpty,
      chatModels.length,
      areCandidatesPrefilled,
      shouldUseLastModelResponses,
      lastModelSelectorContext,
      chatMessages,
      config.systemPrompt,
      config.systemPromptGptOnly,
      config.userPromptTemplate,
      config.templateVariables,
      config.providedChatHistoryJson,
      taskContext,
      step.params.use_first_initial_message,
      step.params.exclude_system_prompt,
      validPrefilledDisplayOrder,
      numCandidates,
      chatCompletionHook,
      role,
      attachments,
      isProcessSupervisionLimitChatHistoryEnabled,
      processSupervisionLimitedChatHistory,
      processSupervisionPromptRole,
      step.params.include_current_turn_in_chat_history,
      previousModelResponse,
      mostRecentPromptForStepId,
      skipPromptInput,
      step.params.appended_system_prompt,
    ],
  );

  const retryCandidate = useCallback(
    async (candidateIndex: number) => {
      const candidate = shuffledCandidates.find((c: any) => c.index === candidateIndex);
      if (!candidate) {
        return;
      }

      const historyChopped = formatChatHistory(chatMessages, {
        systemPrompt: getCombinedSystemPrompt(
          config.systemPrompt,
          step.params.appended_system_prompt,
          step.params.exclude_system_prompt,
        ),
        systemPromptGptOnly: config.systemPromptGptOnly,
        userPromptTemplate: config.userPromptTemplate,
        templateVariables: config.templateVariables,
        alwaysAddSystemPromptAsFirstMessage: taskContext.alwaysAddSystemPromptAsFirstMessage,
        useFirstInitialMessage: step.params.use_first_initial_message,
        resetChatHistoryOnEachTurn: taskContext.resetChatHistoryOnEachTurn,
        providedChatHistoryJson: config.providedChatHistoryJson,
      });

      let skipPromptInputOnRefetch = false;

      if (chatRemoveLastUserMessageIfEmpty && chatPrompt === '') {
        skipPromptInputOnRefetch = true;
      }

      if (isProcessSupervisionLimitChatHistoryEnabled) {
        await chatCompletionHook.fetchCandidate(
          candidate.index,
          {
            role: processSupervisionPromptRole,
            content: chatPrompt,
          },
          processSupervisionLimitedChatHistory,
        );
      } else {
        await chatCompletionHook.fetchCandidate(
          candidate.index,
          {
            role,
            content: chatPrompt,
            ...(attachments.length > 0 ? { attachments } : {}),
            ...(skipPromptInput || skipPromptInputOnRefetch
              ? { skipMessage: skipPromptInput || skipPromptInputOnRefetch }
              : {}),
          },
          historyChopped,
        );
      }
    },
    [
      chatRemoveLastUserMessageIfEmpty,
      shuffledCandidates,
      chatMessages,
      config,
      chatPrompt,
      attachments,
      chatCompletionHook,
      taskContext,
      role,
      step.params.exclude_system_prompt,
      step.params.use_first_initial_message,
      isProcessSupervisionLimitChatHistoryEnabled,
      processSupervisionLimitedChatHistory,
      processSupervisionPromptRole,
      skipPromptInput,
      step.params.appended_system_prompt,
    ],
  );

  // Validate the prompt and generate responses
  useEffect(() => {
    // Needed because fetchCandidates changes when candidates are fully loaded
    if (complete || readOnly || response) {
      return;
    }
    if (!skipPromptInput && !chatPrompt) {
      return;
    }
    if (chatPrompt === submittedPrompt) {
      return;
    }
    if (chatModelsLoading) {
      return;
    }
    validateAndSubmitPrompt(chatPrompt);
  }, [
    chatPrompt,
    complete,
    readOnly,
    response,
    skipPromptInput,
    submittedPrompt,
    validateAndSubmitPrompt,
    chatModelsLoading,
  ]);

  const canSelect = chatCompletionHook.areCandidatesFullyLoaded && !readOnly;
  const isReviewConsensus = reviewLevel === ReviewLevel.ReviewConsensus;

  // Only confirm submission if the response exists and will potentially be changed
  const confirmSubmission = useCallback(() => {
    if (
      response &&
      !isReviewConsensus &&
      step.params.reset_history_on_change &&
      !window.confirm(
        'Are you sure you want to change your selection? This may reset the conversation past this turn if your selected response differs from the existing one.',
      )
    ) {
      return false;
    } else if (
      response &&
      config.turn.find(turn => turn.type === InteractionStepType.ModelResponseEditor) &&
      !window.confirm(
        'Are you sure you want to change your selection? This may affect changes you made to later responses.',
      )
    ) {
      return false;
    }
    return true;
  }, [response, isReviewConsensus, step.params.reset_history_on_change, config.turn]);

  const { minLikert, maxLikert, medianLikert } = useMemo(() => {
    const min = step.params?.min_likert || 1;
    const max = step.params?.max_likert || 7;
    const median = (max - min) / 2 + min;
    return { minLikert: min, maxLikert: max, medianLikert: median };
  }, [step.params?.min_likert, step.params?.max_likert]);

  const [selectedLikertValue, setSelectedLikertValue] = useState<number | null>(
    response?.context?.likertValueRaw ?? null,
  );
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>(
    response?.context?.selectedId || '',
  );
  const [selectedIsObjective, setSelectedIsObjective] = useState<boolean | undefined>(
    response?.context?.isObjective,
  );
  const [responseWasRerolled, setResponseWasRerolled] = useState<boolean>(false);

  const [completedJustificationRaw, setCompletedJustificationRaw] = useState<string>(
    response?.context?.justificationRaw || '',
  );

  const [error, setError] = useState<string>('');

  const maybeSubmitResponse = useCallback(
    (newResponse: {
      candidateId?: string;
      isObjective?: boolean;
      likertValue?: number;
      justificationRaw?: string;
    }) => {
      // TODO @dannyadkins turn this into a helper function called from the parent, remove these props from this component
      if (!confirmSubmission()) {
        return;
      }

      if (chatModelsLoading) {
        return;
      }

      let candidateId: any;

      if (!_.isNil(newResponse?.candidateId)) {
        setSelectedCandidateId(newResponse.candidateId || '');
        candidateId = newResponse.candidateId;
      } else {
        candidateId = selectedCandidateId;
      }

      let isObjective: boolean | undefined;

      if (!_.isNil(newResponse?.isObjective)) {
        setSelectedIsObjective(newResponse?.isObjective);
        isObjective = newResponse?.isObjective;
      } else {
        isObjective = selectedIsObjective;
      }

      let likertValue;

      if (!_.isNil(newResponse?.likertValue)) {
        setSelectedLikertValue(newResponse?.likertValue);
        likertValue = newResponse?.likertValue;
      } else {
        likertValue = selectedLikertValue;
      }

      let justificationRaw;

      if (newResponse?.justificationRaw) {
        setCompletedJustificationRaw(newResponse?.justificationRaw);
        justificationRaw = newResponse?.justificationRaw;
      } else {
        justificationRaw = completedJustificationRaw;
      }

      if (!canSelect) {
        return;
      }

      const candidate = shuffledCandidates.find((candidate: any) => candidate.id === candidateId);

      if (!candidate && _.isNil(likertValue)) {
        return;
      }

      if (step.params.show_objectivity && _.isNil(isObjective)) {
        return;
      }

      if (step.params.show_justification && !justificationRaw?.length) {
        return;
      }

      let output = generateOutput(candidate);

      if (step.params?.always_send_given_id) {
        const forcedCandidate = candidatesWithId.find(
          c => c?.sourceId === step.params?.always_send_given_id,
        );
        if (forcedCandidate) {
          output = forcedCandidate.message.content;
        }
      }

      if (step.params?.use_last_model_responses) {
        const { isValid, errorMessage } = isModelSelectionConsistent(
          textCollectionPerResponseStepResponse,
          textCollectionPerResponseStepParams,
          candidateId,
        );

        if (!isValid) {
          setError(errorMessage);
          return;
        }
      }

      let responseToSubmit: any = {
        type: InteractionStepType.ModelResponseSelector,
        context: {
          messages: chatMessages,
          candidates: candidatesWithId,
          displayOrder: candidateOrder,
          selectedIndex: candidate?.index,
          selectedId: candidateId,
          isObjective,
        },
        output,
      };
      const isReversed = candidateOrder[0] === 1;

      if (step.params?.show_likert_scale && !_.isNil(likertValue)) {
        responseToSubmit = {
          ...responseToSubmit,
          context: {
            ...responseToSubmit.context,
            likertValueRaw: likertValue,
            likertMagnitude: Math.abs(likertValue - medianLikert),
            likertValueUnrandomized: isReversed ? maxLikert - likertValue + 1 : likertValue,
          },
        };
      }

      if (step.params?.show_justification) {
        const responseReferenceRegex = /@Response \d+/g;
        if (!responseReferenceRegex.test(justificationRaw)) {
          setError(
            'Please ensure that your justification contains a reference to a response.\nFor example, you might say "@Response 1 is much better written than @Response 2".',
          );
          return;
        }

        responseToSubmit = {
          ...responseToSubmit,
          context: {
            ...responseToSubmit.context,
            justificationRaw,
            justificationUnrandomized: unrandomizeText(justificationRaw, candidateOrder),
          },
        };
      }

      const shouldResetHistory =
        !isReviewConsensus &&
        step.params.reset_history_on_change &&
        response &&
        (response.output !== output || responseWasRerolled);
      const shouldSkipLinting =
        !step.params.show_likert_scale &&
        !step.params.show_objectivity &&
        !step.params.show_justification;

      onSubmit(
        responseToSubmit,
        responseIndex,
        step,
        shouldResetHistory,
        shouldSkipLinting,
        shouldHackilyUpdateNextMultiTurnContinueStep,
      );
      if (!codeGenerationEnforcementPassed) {
        markStepAsIncompletedByIndex(responseIndex);
      }
      setError('');
      setResponseWasRerolled(false);
    },
    [
      response,
      canSelect,
      candidateOrder,
      candidatesWithId,
      chatMessages,
      completedJustificationRaw,
      onSubmit,
      selectedCandidateId,
      selectedIsObjective,
      selectedLikertValue,
      shuffledCandidates,
      step,
      responseIndex,
      textCollectionPerResponseStepResponse,
      textCollectionPerResponseStepParams,
      confirmSubmission,
      isReviewConsensus,
      maxLikert,
      medianLikert,
      chatModelsLoading,
      responseWasRerolled,
      setResponseWasRerolled,
      codeGenerationEnforcementPassed,
      markStepAsIncompletedByIndex,
      shouldHackilyUpdateNextMultiTurnContinueStep,
    ],
  );

  const initialLikertScore: number | undefined = useMemo(
    () =>
      getInitialLikertScore(
        candidatesWithId,
        step,
        config,
        candidateOrder,
        maxLikert,
        numberOfTurns,
      ),
    [candidatesWithId, step, config, candidateOrder, maxLikert, numberOfTurns],
  );
  const onSelectLikert = useCallback(
    (value: string | number) => {
      let candidate;
      const parsedValue = _.isString(value) ? parseInt(value) : value;

      if (parsedValue < medianLikert) {
        // Sometimes candidateOrder is not defined, so we handle that case
        candidate = candidatesWithId[candidateOrder?.[0] ?? 0];
      } else if (parsedValue > medianLikert) {
        candidate = candidatesWithId[candidateOrder?.[1] ?? 1];
      } else {
        candidate = {
          index: -1,
          id: LikertNoPreferenceId,
          message: {
            content: '',
          },
        };
      }

      if (response && !confirmSubmission()) {
        return;
      }
      maybeSubmitResponse({
        candidateId: candidate.id,
        likertValue: parsedValue,
      });
    },
    [
      medianLikert,
      candidatesWithId,
      candidateOrder,
      response,
      confirmSubmission,
      maybeSubmitResponse,
    ],
  );

  useEffect(() => {
    if (!_.isNil(initialLikertScore) && _.isNil(selectedLikertValue)) {
      onSelectLikert(initialLikertScore);
    }
  }, [initialLikertScore, selectedLikertValue, onSelectLikert]);

  const responseSuggestionsHook = useResponseSuggestions();
  const autoraterStoreKey = useMemo(() => {
    if (
      readOnly ||
      !step.params.show_likert_scale ||
      !mostRecentPrompt ||
      !areAutoraterSuggestionsEnabled ||
      !textCollectionPerResponseStepResponse
    ) {
      return undefined;
    }
    const taskId = taskContext.taskId ?? taskContext.assignmentId;
    return crypto
      .createHash('sha256')
      .update(
        `${taskId}-${mostRecentPrompt}-${JSON.stringify(
          textCollectionPerResponseStepResponse,
        )}-autorater-suggestions`,
      )
      .digest('hex');
  }, [
    readOnly,
    step.params.show_likert_scale,
    mostRecentPrompt,
    areAutoraterSuggestionsEnabled,
    textCollectionPerResponseStepResponse,
    taskContext.taskId,
    taskContext.assignmentId,
  ]);

  useEffect(() => {
    if (!autoraterStoreKey) {
      return;
    }
    responseSuggestionsHook?.fetchLikertScore?.({
      key: autoraterStoreKey,
      prompt: mostRecentPrompt,
      responseDetails: textCollectionPerResponseStepResponse,
      likertScore: selectedLikertValue ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when the cache key changes
  }, [autoraterStoreKey]);

  useEffect(() => {
    if (!autoraterStoreKey) {
      return;
    }
    responseSuggestionsHook?.fetchJustifications?.({
      key: autoraterStoreKey,
      prompt: mostRecentPrompt,
      responseDetails: textCollectionPerResponseStepResponse,
      likertScore: selectedLikertValue ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when the cache key or likert score changes
  }, [autoraterStoreKey, selectedLikertValue]);

  // Note: when updating this function, check if we need to update the logic in handleIndividualReroll
  const handleReroll = useCallback(() => {
    const canUpdateCompletedState = response && !_.isNil(responseIndex);
    if (canUpdateCompletedState) {
      markStepAsIncompletedByIndex(responseIndex);
    }
    setSelectedCandidateId('');
    setSelectedIsObjective(undefined);
    setSelectedLikertValue(null);
    setCompletedJustificationRaw('');
    hasSubmittedRef.current = false;
    validateAndSubmitPrompt(chatPrompt);
    setResponseWasRerolled(true);
  }, [chatPrompt, markStepAsIncompletedByIndex, response, responseIndex, validateAndSubmitPrompt]);

  // Note: when updating this function, check if we need to update the logic in handleReroll
  const handleIndividualReroll = useCallback(
    (candidateIndex: number) => {
      const canUpdateCompletedState = response && !_.isNil(responseIndex);
      if (canUpdateCompletedState) {
        markStepAsIncompletedByIndex(responseIndex);
        // Initialize the candidates with their current content.
        // Note: handleReroll does this differently because it clears the content of all candidates.
        chatCompletionHook.setCandidates(candidates);
      }

      setSelectedCandidateId('');
      setSelectedIsObjective(undefined);
      setSelectedLikertValue(null);
      setCompletedJustificationRaw('');
      setResponseWasRerolled(true);
      // Retry this specific candidate
      retryCandidate(candidateIndex);
    },
    [
      markStepAsIncompletedByIndex,
      response,
      responseIndex,
      retryCandidate,
      chatCompletionHook,
      candidates,
    ],
  );

  // In order to prevent bias, ensure that provided responses don't load in any faster than the model responses
  // We also obfuscate the responses if skip_selection is enabled because the contributors shouldn't be seeing the responses since it's hidden
  const shouldObfuscate =
    (!chatCompletionHook.areCandidatesFullyLoaded && providedResponses.length > 0) ||
    !!step.params.skip_selection;
  const obfuscationLength = useMemo(
    () =>
      _.min([
        ...chatCompletionHook.candidates
          .filter(c => c.sourceId !== EXISTING_CANDIDATE_ID)
          .map(c => c.message.content.length),
      ]),
    [chatCompletionHook.candidates],
  );
  const obfuscate = useCallback(
    (message: string) => {
      return shouldObfuscate ? message.slice(0, obfuscationLength) : message;
    },
    [shouldObfuscate, obfuscationLength],
  );

  // Subscribe to the chat:reroll-individual-model-response event to reroll a specific candidate
  const eventBus = useEventBus();

  useEffect(() => {
    const subscription = eventBus.on('chat:reroll-individual-model-response').subscribe(payload => {
      const { candidateId, previousModelResponseSelectorStepId } = payload.data;
      if (previousModelResponseSelectorStepId === step.id) {
        const candidateIndex = candidatesWithId.find(c => c.id === candidateId)?.index;
        if (!_.isNil(candidateIndex)) {
          handleIndividualReroll(candidateIndex);
        }
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [eventBus, candidatesWithId, handleIndividualReroll, step.id]);

  // Submit as soon as responses are generated or loaded in if skip selection is enabled
  useEffect(() => {
    if (
      canSelect &&
      !!step.params.skip_selection &&
      shuffledCandidates.length > 0 &&
      !complete &&
      !chatModelsLoading
    ) {
      let responseToSubmit: any = {
        type: InteractionStepType.ModelResponseSelector,
        context: {
          messages: chatMessages,
          candidates: candidatesWithId,
          displayOrder: candidateOrder,
          selectedIndex: -1,
          selectedId: '',
        },
        output:
          shuffledCandidates.length === 1
            ? generateOutput(shuffledCandidates[0])
            : lastMessage.content,
      };

      if (step.params?.show_likert_scale) {
        responseToSubmit = {
          ...responseToSubmit,
          context: {
            ...responseToSubmit.context,
            likertValueRaw: undefined,
            likertMagnitude: undefined,
            likertValueUnrandomized: undefined,
          },
        };
      }

      onSubmit(
        responseToSubmit,
        responseIndex,
        step,
        undefined,
        // skip linting during submission
        true,
      );
    }
  }, [
    canSelect,
    candidateOrder,
    candidatesWithId,
    complete,
    shuffledCandidates,
    step.params.skip_selection,
    chatMessages,
    onSubmit,
    chatModelsLoading,
    responseIndex,
    step,
    lastMessage.content,
    shouldHackilyUpdateNextMultiTurnContinueStep,
  ]);

  // Submit as soon as the response is generated if there is only one response and skip selection is not enabled
  useEffect(() => {
    if (
      canSelect &&
      !step.params.skip_selection &&
      shuffledCandidates.length === 1 &&
      !complete &&
      !chatModelsLoading &&
      codeGenerationEnforcementPassed
    ) {
      let responseToSubmit: any = {
        type: InteractionStepType.ModelResponseSelector,
        context: {
          messages: chatMessages,
          candidates: candidatesWithId,
          displayOrder: candidateOrder,
          selectedIndex: shuffledCandidates[0].index,
          selectedId: shuffledCandidates[0].id,
        },
        output: generateOutput(shuffledCandidates[0]),
      };

      if (step.params?.show_likert_scale) {
        responseToSubmit = {
          ...responseToSubmit,
          context: {
            ...responseToSubmit.context,
            likertValueRaw: undefined,
            likertMagnitude: undefined,
            likertValueUnrandomized: undefined,
          },
        };
      }

      onSubmit(
        responseToSubmit,
        responseIndex,
        step,
        undefined,
        // skip linting during submission
        true,
        shouldHackilyUpdateNextMultiTurnContinueStep,
      );
    }
  }, [
    canSelect,
    candidateOrder,
    candidatesWithId,
    chatMessages,
    complete,
    onSubmit,
    shuffledCandidates,
    chatModelsLoading,
    responseIndex,
    step,
    codeGenerationEnforcementPassed,
    shouldHackilyUpdateNextMultiTurnContinueStep,
  ]);

  // Diff is only supported for exactly two candidates
  const showTwoCandidateDiffResult =
    shuffledCandidates.length === 2 && chatCompletionHook.areCandidatesFullyLoaded;

  // Hide the selector step completely if skip_selection is enabled and response generation is complete
  if (step.params.skip_selection && complete) {
    return null;
  }

  return (
    <CardAlert message={response && !complete ? 'Please save the section below.' : undefined}>
      <Transition
        show
        enter="transition-all duration-300"
        enterFrom="opacity-0 -translate-y-4"
        enterTo="opacity-100 translate-y-0 "
        leave="transition-all duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-4"
      >
        <StepWidthWrapper
          width={
            isPinnedView
              ? StepWidth.Full
              : numCandidates === 1 || taskContext.enableUnifiedFeedback
              ? StepWidth.Responsive
              : StepWidth.Fit
          }
        >
          <ModelResponseSelectorTabsWrapper className="w-full">
            {({ tab, setTab, stacked, setStacked }) => (
              <StepCard readOnly={readOnly}>
                <ModelResponseSelectorCardHeader
                  step={step}
                  responseIndex={responseIndex}
                  showTwoCandidateDiffResult={!!showTwoCandidateDiffResult}
                  tab={tab}
                  setTab={setTab}
                  isPinnable={isPinnable}
                  isPinnedView={!!isPinnedView}
                  chatMessageDisplayConfig={chatMessageDisplayConfig}
                  useMarkdownDisplay={useMarkdownDisplay}
                  updateMarkdownConfig={updateMarkdownConfig}
                  toggleChatMessageDisplayType={toggleChatMessageDisplayType}
                  readOnly={!!readOnly}
                  usedForProcessSupervision={!!allProcessSupervisionResponses?.length}
                  stacked={stacked}
                  setStacked={setStacked}
                  chatPrompt={chatPrompt}
                />
                <StepCard.Body className="p-4">
                  <CandidateCards
                    candidates={shuffledCandidates}
                    selectedCandidateId={selectedCandidateId}
                    onCandidateSelect={candidateId => {
                      maybeSubmitResponse({ candidateId });
                    }}
                    reactGeneratedUrlWithMetadataByModel={reactGeneratedUrlWithMetadataByModel}
                    onSetReactGeneratedUrlWithMetadata={(candidateId, urlWithMetadata) => {
                      setReactGeneratedUrlWithMetadataByModel({
                        ...reactGeneratedUrlWithMetadataByModel,
                        [candidateId]: urlWithMetadata,
                      });
                      if (response) {
                        const responseToSubmit = {
                          ...response,
                          context: {
                            ...response.context,
                            candidates: candidatesWithId.map(candidate => {
                              if (candidate.id === candidateId) {
                                return {
                                  ...candidate,
                                  ...urlWithMetadata,
                                };
                              }
                              return candidate;
                            }),
                          },
                        };
                        onSubmit(
                          responseToSubmit,
                          responseIndex,
                          step,
                          false,
                          true,
                          shouldHackilyUpdateNextMultiTurnContinueStep,
                        );
                      }
                    }}
                    disabled={
                      !canSelect ||
                      step.params.show_likert_scale ||
                      step.params.disable_selection_show_step
                    }
                    isReviewConsensus={isReviewConsensus}
                    enableRTL={step.params.enable_rtl_text!}
                    useMarkdownDisplay={useMarkdownDisplay}
                    candidatesAreLoaded={chatCompletionHook.areCandidatesFullyLoaded}
                    chatMessageDisplayConfig={chatMessageDisplayConfig}
                    tab={tab}
                    obfuscate={obfuscate}
                    retryCandidate={retryCandidate}
                    retryCooldown={chatCompletionHook.refetchCandidateCooldown}
                    numRetries={chatCompletionHook.numRetries}
                    exitTask={taskContext.exitTask}
                    chatModelsLoading={chatModelsLoading}
                    chatModelsError={chatModelsError}
                    chatPrompt={chatPrompt}
                    enableModelCritique={!readOnly && step.params.enable_model_critique!}
                    stacked={stacked}
                    individualRerollEnabled={
                      step.params.allow_reroll && step.params.allow_reroll_for_individual_candidates
                    }
                    enableReactUIRendering={step.params.enable_react_ui_rendering}
                    extractModelResponseRegex={step.params.extract_model_response_regex}
                    handleIndividualReroll={handleIndividualReroll}
                    forceLoadingStateForAllCandidates={!!step.params.skip_selection}
                    enableUiGeneration={step.params.enable_ui_generation}
                    uiGenerationEnforcementMode={step.params.ui_generation_enforcement_mode}
                    setCodeGenerationEnforcementPassed={setCodeGenerationEnforcementPassed}
                  />
                  {!step.params.show_likert_scale && (
                    <TutorialFieldContainer
                      stepIndex={responseIndex}
                      fieldId="selectedIndex"
                      response={
                        shuffledCandidates.find(
                          (candidate: any) => candidate.id === selectedCandidateId,
                        )?.index ?? -1
                      }
                    />
                  )}
                  {!readOnly &&
                    step.params.allow_reroll &&
                    !step.params.allow_reroll_for_individual_candidates &&
                    (!step.params.allow_reroll_for_reviewers_only ||
                      reviewLevel !== ReviewLevel.Attempt) && (
                      <div className="mt-4">
                        <Button
                          disabled={
                            !chatCompletionHook.areCandidatesFullyLoaded ||
                            chatCompletionHook.refetchCandidateCooldown > 0
                          }
                          variant="secondary"
                          size="sm"
                          onClick={handleReroll}
                        >
                          {chatCompletionHook.refetchCandidateCooldown > 0
                            ? `Please wait ${pluralize(
                                'second',
                                chatCompletionHook.refetchCandidateCooldown,
                                true,
                              )}`
                            : 'Fetch New Responses'}
                        </Button>
                      </div>
                    )}
                  {step.params.show_likert_scale && (
                    <>
                      <LikertScale
                        minLikert={minLikert}
                        maxLikert={maxLikert}
                        customLabels={step.params.custom_likert_labels}
                        selectedLikertValue={selectedLikertValue}
                        onSelect={onSelectLikert}
                        readOnly={!!readOnly || chatModelsLoading || anyCandidateIsLoading}
                        suggestions={
                          areAutoraterSuggestionsEnabled
                            ? responseSuggestionsHook?.suggestions
                            : undefined
                        }
                        suggestionsLoading={
                          areAutoraterSuggestionsEnabled
                            ? responseSuggestionsHook?.likertScoreLoading
                            : false
                        }
                      />
                      <TutorialFieldContainer
                        stepIndex={responseIndex}
                        fieldId="likertValueRaw"
                        response={selectedLikertValue} // TODO: ask @danny about this
                      />
                    </>
                  )}
                  {step.params.show_objectivity &&
                    !step.params.skip_selection &&
                    (selectedLikertValue || !step.params.show_likert_scale) && (
                      <ObjectivityQuestion
                        selectedIsObjective={selectedIsObjective!}
                        setSelectedIsObjective={(value: boolean) => {
                          maybeSubmitResponse({ isObjective: value });
                        }}
                      />
                    )}
                  {step.params.show_justification &&
                    selectedCandidateId &&
                    // Check if the objectivity is complete, OR if it's not shown at all
                    (!step.params.show_objectivity || selectedIsObjective !== undefined) && (
                      <>
                        <JustificationEditor
                          value={completedJustificationRaw}
                          setValue={setCompletedJustificationRaw}
                          onSubmit={(value: string) => {
                            maybeSubmitResponse({ justificationRaw: value });
                          }}
                          mentionCandidates={candidateOrder.map((candidateIndex, responseIndex) => {
                            const candidate = candidatesWithId.find(
                              c => c.index === candidateIndex,
                            )!;
                            const name = 'Response ' + (responseIndex + 1);
                            const id = candidate.id;
                            const subtext = candidate.message.content;
                            return { name, id, subtext };
                          })}
                          response={response}
                          readOnly={!!readOnly}
                          complete={complete}
                          suggestions={
                            areAutoraterSuggestionsEnabled
                              ? responseSuggestionsHook?.suggestions
                              : undefined
                          }
                          suggestionsLoading={
                            areAutoraterSuggestionsEnabled
                              ? responseSuggestionsHook?.justificationLoading
                              : false
                          }
                        />
                        <TutorialFieldContainer
                          fieldId="justification"
                          stepIndex={responseIndex}
                          response={response?.context?.justificationRaw || ''}
                        />
                      </>
                    )}
                  {error && (
                    <Alert variant="danger" className="mt-2">
                      <Typography.Paragraph2>{error}</Typography.Paragraph2>
                    </Alert>
                  )}
                </StepCard.Body>
                {!!step.params.disable_selection_show_step && (
                  <StepCard.Footer className="flex justify-end">
                    <Tooltip
                      text={
                        response
                          ? 'You already acknowledged this response'
                          : 'Please wait for all model responses to load'
                      }
                      side="top"
                      disabled={canSelect && !response}
                      target={
                        <div>
                          <Button
                            onClick={async () => {
                              let responseToSubmit: any = {
                                type: InteractionStepType.ModelResponseSelector,
                                context: {
                                  messages: chatMessages,
                                  candidates: candidatesWithId,
                                  displayOrder: candidateOrder,
                                  selectedIndex: -1,
                                  selectedId: '',
                                },
                                output:
                                  shuffledCandidates.length === 1
                                    ? generateOutput(shuffledCandidates[0])
                                    : lastMessage.content,
                              };

                              if (step.params?.show_likert_scale) {
                                responseToSubmit = {
                                  ...responseToSubmit,
                                  context: {
                                    ...responseToSubmit.context,
                                    likertValueRaw: undefined,
                                    likertMagnitude: undefined,
                                    likertValueUnrandomized: undefined,
                                  },
                                };
                              }

                              onSubmit(
                                responseToSubmit,
                                responseIndex,
                                step,
                                undefined,
                                // skip linting during submission
                                true,
                                shouldHackilyUpdateNextMultiTurnContinueStep,
                              );
                            }}
                            fullWidth
                            disabled={!canSelect || !!response}
                            data-testid="submit-mrs-button"
                          >
                            Continue
                          </Button>
                        </div>
                      }
                    />
                  </StepCard.Footer>
                )}
              </StepCard>
            )}
          </ModelResponseSelectorTabsWrapper>
        </StepWidthWrapper>
      </Transition>
    </CardAlert>
  );
};

function getCandidateOrder(taskContext: ChatTaskContextType, numCandidates: number): number[] {
  const randomizationEnabledInConfig = !!taskContext?.modelResponseConfig?.enableOrderRandomization;
  if (randomizationEnabledInConfig && !taskContext.responseTemplatingMode) {
    return generateShuffledRange(numCandidates);
  }
  return _.range(numCandidates);
}

// TODO @dannyadkins memoize the components

export default ModelResponseSelectorTaskComponent;
